<template>
  <div class="date-planner">
    <div class="date-planner__title">Content Date Planner</div>
    <div class="date-planner__subtitle">The planner is an easy way to find a URL without a date and drag it to the
      calendar
    </div>
    <div class="date-planner__status-wrapper">
      <span class="date-planner__status-title">URL Status</span>
      <div class="date-planner__checkbox-wrapper">

        <base-check-box class="date-planner__checkbox date-planner__checkbox_first"
                        v-model="selectedCheckboxes.planned.selected"
                        :trueValue="selectedCheckboxes.planned.id"
                        :falseValue="null"
                        @change="onStatusSelected"
                        label="Planned"/>
        <base-check-box class="date-planner__checkbox"
                        v-model="selectedCheckboxes.proposed.selected"
                        :trueValue="selectedCheckboxes.proposed.id"
                        @change="onStatusSelected"
                        :falseValue="null"
                        label="Proposed"/>
      </div>
    </div>
    <div class="date-planner__status-wrapper">
      <span class="date-planner__status-title">Category</span>
      <base-check-box class="date-planner__checkbox date-planner__checkbox-wrapper"
                      v-model="selectedCheckboxes.category.selected"
                      :trueValue="selectedCheckboxes.category.id"
                      :falseValue="null"
                      @change="onEditorialSelected"
                      label="Editorial Only"/>

    </div>
    <div class="date-planner__input-wrapper">
      <input class="date-planner__input" v-model="filters.url"
             v-on:keyup.enter="onSearchClick"
      >
      <base-material-design-icon icon="search"
                                 class="date-planner__input-search"
                                 :class="{'date-planner__input-search_disabled': isSearchDisabled}"
                                 @click="onSearchClick"/>

    </div>

    <div class="date-planner__list" id="url-list">
      <div class="date-planner__item" v-for="url in urls" :key="url._id"
           v-tooltip.bottom-start="{content: url.url, ...tooltipOptions}"
           v-payload="url">
           <base-truncate-label
                    :label="url.url"
                    :shrink="0.90"
                    watchClass="date-planner__item"/>
      </div>
    </div>
    <div class="date-planner__controls">
      <button class="date-planner__button date-planner__button_cancel"
              @click.prevent="$emit('navigation', 'calendar')">Close
      </button>

    </div>

    <div class="date-planner__update-loader" v-if="isDataUpdating">
      <base-loader class="date-planner__update-loader-ring"/>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import _ from 'lodash'
import { ALLOWED_URL_STATUS_NAMES_CALENDAR_PLANNER } from '@/common/AllowedUrlStatuses'
import { Draggable } from '@fullcalendar/interaction'
// eslint-disable-next-line no-unused-vars
import Calendar from './index'

const EMPTY_CHECKBOXES = {
  proposed: {
    id: null,
    selected: null
  },
  planned: {
    id: null,
    selected: null
  },
  category: {
    id: null,
    selected: null
  }
}
const EMPTY_FILTERS = {
  url_status: [],
  category: [],
  url: null,
  allowed_statuses: []
}

export default {
  name: 'CalendarDatePlanner',
  beforeDestroy () {
    this.reset()
  },
  data () {
    return {
      // object that managed planned and proposed checkboxes
      selectedCheckboxes: _.cloneDeep({ ...EMPTY_CHECKBOXES }),
      filters: _.cloneDeep({ ...EMPTY_FILTERS }),
      // to disable search when input does not changed
      previousSearch: null,
      isDataUpdating: false,
      urls: [],
      draggableContainer: null,
      timeoutId : null
    }
  },
  watch: {
    urlStatuses: {
      handler (value, oldValue) {
        const oldValueLength = oldValue ? oldValue.length : null
        // recalculate if data was changed
        if (value && value.length && value.length !== oldValueLength) {
          // create allowed_satuses filter based on received url statuses and allowed url status names for calendar planner
          this.selectedCheckboxes.planned.id = _.get(this.urlStatuses.find(item => item.label.toLowerCase() === 'planned'), '_id', null)
          this.selectedCheckboxes.proposed.id = _.get(this.urlStatuses.find(item => item.label.toLowerCase() === 'proposed'), '_id', null)
          const allowedStatusIds = this.urlStatuses
            .filter(item => ALLOWED_URL_STATUS_NAMES_CALENDAR_PLANNER.includes(item.label.toLowerCase()))
            .map(item => {
              return item._id
            })
          this.filters.allowed_statuses = allowedStatusIds
          if(this.timeoutId){
            clearTimeout(this.timeoutId)
          }
          this.timeoutId = setTimeout(this.loadData, 1000)
        }
      },
      immediate: true
    },
    pageTypes: {
      handler (value, oldValue) {
        const oldValueLength = oldValue ? oldValue.length : null
        if (value && value.length && value.length !== oldValueLength) {
          const editorialId = _.get(this.pageTypes.find(item => item.label.toLowerCase() === 'editorial'), '_id', null);
          this.selectedCheckboxes.category.id = editorialId
          this.selectedCheckboxes.category.selected = editorialId
          this.filters.category = editorialId
          if(this.timeoutId){
            clearTimeout(this.timeoutId)
          }
          this.timeoutId = setTimeout(this.loadData, 1000)
        }
      },
      immediate: true
    },
    noDateUrls (value) {
      this.urls = _.cloneDeep(value)
      // after receiving the date we need to make list item draggable
      this.$nextTick(() => {
        let draggableEl = document.getElementById('url-list')
        if (draggableEl) {
          try {
            this.draggableContainer && this.draggableContainer.destroy()
            this.draggableContainer = new Draggable(draggableEl, {
              itemSelector: '.date-planner__item'
            })
          } catch (e) {
            console.log(e)
          }
        }
      })
    }
  },
  computed: {
    ...mapState(['urlStatuses']),
    ...mapState('rankZoneCommon', ['pageTypes']),
    ...mapState('urlPlanning', ['noDateUrls']),
    isSearchDisabled () {
      return this.previousSearch === this.filters.url
    }
  },
  methods: {
    ...mapActions('urlPlanning', ['getNoDateUrls']),
    async loadData () {
      this.isDataUpdating = true
      await this.getNoDateUrls({
        clientId: this.clientId,
        filters: this.filters
      })
      this.isDataUpdating = false
    },
    onSearchClick () {
      if (this.previousSearch !== this.filters.url) {
        this.previousSearch = this.filters.url
        this.loadData()
      }
    },
    onStatusSelected () {
      const statuses = []
      this.selectedCheckboxes.planned.selected && statuses.push(this.selectedCheckboxes.planned.selected)
      this.selectedCheckboxes.proposed.selected && statuses.push(this.selectedCheckboxes.proposed.selected)
      const different = this.filters.url_status.length !== statuses.length || _.difference(this.filters.url_status, statuses)
      // load data only if selected statuses was changed
      if (different) {
        this.filters.url_status = statuses
        this.loadData()
      }
    },
    onEditorialSelected(){
      this.filters.category = this.selectedCheckboxes.category.selected
      this.loadData()
    },
    /**
     * remove url from {@link Calendar}
     */
    removeUrl (id) {
      this.urls.splice(this.urls.findIndex(item => item._id === id), 1)
    },
    /**
     * for external reset the components data. Not used now
     */
    reset () {
      this.selectedCheckboxes = _.cloneDeep({ ...EMPTY_CHECKBOXES })
      this.filters = _.cloneDeep({ ...EMPTY_FILTERS })
      this.previousSearch = null
    }
  }
}
</script>

<style scoped lang="scss">

  .date-planner {
    position: relative;
    padding: 30px;
    background-color: $rfx-date-planner-background;
    display: flex;
    flex-direction: column;

    &__title {
      font-size: 18px;
      font-weight: 500;
    }

    &__subtitle {
      font-size: 12px;
      font-weight: 500;
      margin-top: 10px;
      word-spacing: 2px;
    }

    &__input-wrapper {
      position: relative;
      width: 100%;
      margin-bottom: 30px;
    }

    &__checkbox-wrapper{
      position: absolute;
      margin-left: 80px;
    }

    &__input-search {
      position: absolute;
      font-size: 1.8em;
      top: 12px;
      right: 10px;
      cursor: pointer;

      &_disabled {
        pointer-events: none;
      }
    }

    &__checkbox {
      flex: 0 0;

      &_first {
        margin-right: 30px;
      }
    }

    &__status-wrapper {
      display: flex;
      margin: 20px 0 10px;
      justify-content: space-between;
      align-items: center;
      position: relative;
      min-width: 345px;
    }

    &__status-title {
      font-size: 12px;
      font-weight: bold;
      flex: 1 1;
    }

    &__status-chips {

    }

    &__list {
      height: 100%;
      border: solid 2px $rfx-light-steel;
      background-color: #fff;
      padding: 15px;
      overflow-y: auto;
      /*max-width: 355px;*/
    }

    &__item {
      color: $rfx-bright-blue;
      padding: 10px 0;
      white-space: nowrap;
      overflow: hidden;
      /*text-overflow: ellipsis;*/
      cursor: move;
      /*text-align: right;*/
    }

    &__input {
      width: 100%;
      height: 50px;
      border-radius: 4px;
      border: solid 2px $rfx-light-steel;
      background-color: #fff;
      box-sizing: border-box;
      padding: 0 40px 0 10px;
      font-size: 12px;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_cancel {
        background-color: $rfx-medium-march;
        color: #fff;
      }
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }
  }

</style>
